import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Camera, KeyRound, Square } from "lucide-react";
import { Html5Qrcode } from "html5-qrcode";
import { itemsApi } from "@/lib/catalog-api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ApiError } from "@/lib/api";
import { ScanErrorBoundary } from "@/components/catalog/ScanErrorBoundary";

type ScannerState = "idle" | "starting" | "running" | "stopping" | "stopped";

const CONTAINER_ID = "wi-qr-reader";

/** Defensive scanner viewport — never throws to the parent.
 *
 * Browsers gate camera access on a user gesture, so we deliberately do NOT
 * auto-start. The user clicks "Start camera" to begin, and we only call
 * scanner.stop() when state is 'running' (otherwise html5-qrcode raises
 * "Cannot stop, scanner is not running or paused").
 */
function ScannerViewport({ active, onDecode, onError }: {
  active: boolean;
  onDecode: (text: string) => void;
  onError: (msg: string) => void;
}) {
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const stateRef = useRef<ScannerState>("idle");
  const lastDecodedRef = useRef<string | null>(null);
  const [running, setRunning] = useState(false);

  async function safeStop() {
    const s = scannerRef.current;
    if (!s) return;
    if (stateRef.current !== "running") return;
    stateRef.current = "stopping";
    try {
      await s.stop();
    } catch (e) {
      // Swallow "Cannot stop, scanner is not running or paused".
      // eslint-disable-next-line no-console
      console.debug("[Scan] stop swallowed", e);
    }
    try { s.clear(); } catch { /* */ }
    stateRef.current = "stopped";
    setRunning(false);
  }

  useEffect(() => {
    if (!active) {
      // Caller toggled off — stop if we were running.
      safeStop();
      return;
    }
    if (stateRef.current !== "idle" && stateRef.current !== "stopped") return;

    // Build (or rebuild) the html5-qrcode instance.
    let cancelled = false;
    let scanner: Html5Qrcode;
    try {
      scanner = new Html5Qrcode(CONTAINER_ID, { verbose: false });
    } catch (e) {
      onError(e instanceof Error ? e.message : "Could not initialise the scanner.");
      return;
    }
    scannerRef.current = scanner;
    stateRef.current = "starting";

    Html5Qrcode.getCameras()
      .then((cams) => {
        if (cancelled) return;
        if (!cams || cams.length === 0) {
          stateRef.current = "stopped";
          onError("No camera found on this device. Use the manual code below.");
          return;
        }
        const preferred = cams.find((c) => /back|rear|environment/i.test(c.label)) || cams[0];
        return scanner.start(
          preferred.id,
          { fps: 10, qrbox: { width: 240, height: 240 } },
          (decoded) => {
            if (cancelled) return;
            if (lastDecodedRef.current === decoded) return;
            lastDecodedRef.current = decoded;
            onDecode(decoded);
          },
          () => { /* per-frame errors are noisy, ignore */ }
        );
      })
      .then(() => {
        if (cancelled) {
          // The component flipped active=false before start completed — stop now.
          stateRef.current = "running";
          safeStop();
          return;
        }
        stateRef.current = "running";
        setRunning(true);
      })
      .catch((e: unknown) => {
        stateRef.current = "stopped";
        const msg = e instanceof Error ? e.message : "Camera unavailable.";
        const friendly =
          /not allowed|permission|denied/i.test(msg) ?
            "Camera permission was blocked. Allow it in your browser, or use the manual code below." :
          /not found|no devices/i.test(msg) ?
            "No camera detected on this device. Use the manual code below." :
            `Camera error: ${msg}. Use the manual code below.`;
        onError(friendly);
      });

    return () => {
      cancelled = true;
      // Defer the actual stop so it sees the latest state ref.
      const s = scannerRef.current;
      scannerRef.current = null;
      if (s && stateRef.current === "running") {
        stateRef.current = "stopping";
        Promise.resolve(s.stop()).catch(() => {}).finally(() => {
          try { s.clear(); } catch { /* */ }
          stateRef.current = "stopped";
        });
      } else if (s) {
        try { s.clear(); } catch { /* */ }
        stateRef.current = "stopped";
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  return (
    <div className="border border-border bg-bone/30 p-4 sm:p-6" data-testid="scan-camera-section">
      <div
        id={CONTAINER_ID}
        data-testid="scan-camera-container"
        className="w-full aspect-square max-w-md mx-auto bg-charcoal/5"
      />
      <p className="text-xs text-muted-foreground text-center mt-4">
        {active ? (running ? "Looking for a code…" : "Requesting camera…") : "Camera off."}
      </p>
    </div>
  );
}

export default function Scan() {
  const navigate = useNavigate();
  const [cameraOn, setCameraOn] = useState(false); // user gesture required to turn on
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [resolving, setResolving] = useState(false);

  async function resolve(raw: string) {
    setResolving(true);
    setError(null);
    try {
      const trimmed = raw.trim().toUpperCase();
      const res = await itemsApi.byCode(trimmed);
      // Stop camera before nav so the device frees the stream.
      setCameraOn(false);
      navigate(`/app/items/${res.id}`);
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) {
        setError(`No item matches code "${raw.trim().toUpperCase()}".`);
      } else {
        setError(e instanceof Error ? e.message : "Lookup failed");
      }
    } finally {
      setResolving(false);
    }
  }

  return (
    <main className="flex-1 px-6 md:px-10 py-10" data-testid="scan-page">
      <div className="max-w-2xl">
        <p className="text-[11px] uppercase tracking-[0.28em] text-oxblood font-medium mb-3">— Scan</p>
        <h1 className="font-display text-3xl sm:text-4xl">Scan an item.</h1>
        <p className="mt-3 text-sm text-muted-foreground max-w-prose">
          Point your camera at the QR on the garment label or rack tag. We'll open the
          item the moment we read it. No reception? Type the code on the label.
        </p>

        {/* Camera section — wrapped in an error boundary so a runtime crash
            never hides the manual fallback below. */}
        <div className="mt-8">
          <div className="flex items-center justify-between mb-3">
            <p className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
              <Camera className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" /> Camera
            </p>
            <Button type="button" variant="outline" size="sm"
              data-testid="scan-camera-toggle"
              onClick={() => { setError(null); setCameraOn((v) => !v); }}
              className="rounded-none h-8 text-xs">
              {cameraOn ? (<><Square className="w-3.5 h-3.5 mr-1.5" />Stop camera</>) : "Start camera"}
            </Button>
          </div>
          <ScanErrorBoundary>
            {cameraOn ? (
              <ScannerViewport
                active={cameraOn}
                onDecode={(t) => resolve(t)}
                onError={(m) => { setError(m); setCameraOn(false); }}
              />
            ) : (
              <div className="border border-dashed border-border p-6 text-sm text-muted-foreground text-center"
                data-testid="scan-camera-placeholder">
                Camera is off. Tap <span className="text-foreground">Start camera</span> to scan,
                or type a code below — both work.
              </div>
            )}
          </ScanErrorBoundary>
        </div>

        {/* Manual entry — ALWAYS visible, outside the error boundary, so any
            scanner crash leaves this fully usable. */}
        <form
          onSubmit={(e) => { e.preventDefault(); if (code.trim()) resolve(code); }}
          data-testid="scan-manual-form"
          className="mt-8 max-w-md"
        >
          <p className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground mb-3">
            <KeyRound className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" /> Or type a code
          </p>
          <div className="flex gap-2">
            <Input
              data-testid="scan-manual-input"
              placeholder="WI-A1B2"
              value={code}
              onChange={(e: any) => setCode(e.target.value)}
              className="h-11 font-mono text-base tracking-widest"
            />
            <Button
              type="submit"
              data-testid="scan-manual-submit"
              disabled={!code.trim() || resolving}
              className="bg-oxblood hover:bg-oxblood-deep text-ivory rounded-none h-11 px-6 shrink-0"
            >
              {resolving ? "Looking up…" : "Open"}
            </Button>
          </div>
        </form>

        {error && (
          <div data-testid="scan-error" role="status"
            className="mt-6 max-w-md border border-oxblood/30 bg-oxblood/5 px-4 py-3 text-sm text-oxblood-deep flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}
      </div>
    </main>
  );
}
